import React, { useState } from "react";
import { SchoolConfig } from "../types/school";
import LoginPanel from "./LoginPanel";
import Footer from "./Footer";

interface LoginPageProps {
  config: SchoolConfig;
}

const LoginPage: React.FC<LoginPageProps> = ({ config }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAuthSuccess = (authData: any) => {
    console.log("Authentication successful:", authData);
    console.log("Config:", config);
    console.log("Access Token:", authData.accessToken);
    console.log("User:", authData.user);
    console.log("Tenant ID:", config.tenantId);

    // Create URL parameters to pass auth data between ports
    const authParams = new URLSearchParams({
      token: authData.accessToken,
      refreshToken: authData.refreshToken,
      role: authData.user.role,
      tenantKey: config.tenantId,
      username: authData.user.username,
    });

    // Redirect to the main app with auth parameters
    const redirectUrl = `${config.redirectUrl}/login?${authParams.toString()}`;
    console.log("Redirecting to:", redirectUrl);

    // Redirect immediately - no localStorage needed
    window.location.href = redirectUrl;
  };

  const handleAuthError = (error: Error) => {
    console.error("Authentication failed:", error);
    setError(error.message);
    setIsLoading(false);

    // Auto-clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  };

  const handleAuthStart = () => {
    setIsLoading(true);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-blue-900 flex flex-col">
      {/* Main Glass Container */}
      <div className="flex-1 flex items-center justify-center p-3 lg:p-6">
        <div className="w-full max-w-5xl bg-white/10 backdrop-blur-2xl rounded-[24px] overflow-hidden shadow-2xl border border-white/20 grid lg:grid-cols-2 h-[calc(100vh-6rem)] max-h-[580px]">
          {/* Left Hero Section - Simplified */}
          <div className="relative hidden lg:flex items-center justify-center bg-gradient-to-br from-blue-700/60 to-indigo-900/70">
            {/* Floating circles */}
            <div className="absolute top-8 left-8 w-32 h-32 bg-cyan-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-8 right-8 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl"></div>

            <div className="relative z-10 px-8">
              {/* School Image Only */}
              <img
                src={
                  config.images.heroImage ||
                  config.images.backgroundImage ||
                  "https://images.unsplash.com/photo-1509062522246-3755977927d7"
                }
                alt="School"
                className="rounded-2xl shadow-xl object-cover h-[280px] w-full"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "https://images.unsplash.com/photo-1509062522246-3755977927d7";
                }}
              />
            </div>
          </div>

          {/* Right Login Section */}
          <div className="bg-white p-6 lg:p-8 flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="mb-6 text-center">
                <img
                  src={config.images.logo}
                  alt={config.name}
                  className="h-12 w-auto mx-auto mb-4"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-slate-500">
                  Login to continue to your school portal
                </p>
              </div>

              <LoginPanel
                config={config}
                isLoading={isLoading}
                error={error}
                onAuthSuccess={handleAuthSuccess}
                onAuthError={handleAuthError}
                onAuthStart={handleAuthStart}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <Footer config={config} />
    </div>
  );
};

export default LoginPage;
